import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { UserProfile, AccessLevel, UserGroup } from '../types';
import { controllo_congruenza_account } from '../lib/dbHelper';
import { toast } from 'sonner';
import { getPersonPhotoPath } from '../lib/utils';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  isProcessing: boolean;
  signIn: () => Promise<void>;
  signOut: (reason?: 'manual' | 'timeout') => Promise<void>;
  refreshProfile: () => Promise<void>;
  authorizeWorkspace?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const userRef = React.useRef<UserProfile | null>(null);
  
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loginError, setLoginError] = useState<{ title: string; message: string } | null>(null);
  const fetchInProgress = React.useRef<string | null>(null);
  const lastFetchedUserId = React.useRef<string | null>(null);
  const successReceivedRef = React.useRef<boolean>(false);
  const isAuthorizingWorkspaceRef = React.useRef<boolean>(false);

  const fetchProfile = React.useCallback(async (supabaseUser: any, session?: any, forceRefresh: boolean = false) => {
    if (!supabaseUser) {
      setLoading(false);
      setIsProcessing(false);
      return;
    }
    
    // Se abbiamo già il profilo caricato e l'ID è lo stesso, non facciamo nulla
    // a meno che non sia un refresh forzato
    if (!forceRefresh && userRef.current && userRef.current.id === supabaseUser.id && !userRef.current.authError) {
      setLoading(false);
      setIsProcessing(false);
      return;
    }

    // Lock per evitare fetch paralleli dello stesso profilo
    if (fetchInProgress.current === supabaseUser.id) {
      return;
    }

    fetchInProgress.current = supabaseUser.id;
    
    // Se non siamo già in "processing" (es. attivato da signIn), attiviamolo
    setIsProcessing(true);
    
    try {
      // 1. Verifica Dominio
      if (!supabaseUser.email?.endsWith('@gevbologna.org')) {
        console.error('useAuth: [fetchProfile] Dominio non autorizzato:', supabaseUser.email);
        await supabase.auth.signOut();
        setUser(null);
        return;
      }

      // 2. Recupero Sessione
      let currentSession = session;
      if (!currentSession) {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("useAuth: [fetchProfile] Errore getSession:", error);
          throw error;
        }
        currentSession = data.session;
      }
      
      let groups: UserGroup[] = [];
      let authError: string | undefined = undefined;

      // 3. Sincronizzazione Gruppi Google via Backend
      try {
        const controller = new AbortController();
        // Ridotto il timeout a 4s per essere più reattivi durante i riavvii del server
        const timeoutId = setTimeout(() => controller.abort(), 4000);

        const response = await fetch(`/api/groups?email=${encodeURIComponent(supabaseUser.email)}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.groups) {
            groups = data.groups.map((g: any) => ({
              name: g.name,
              email: g.email,
              role: 'Membro',
              membersCount: g.directMembersCount ? parseInt(g.directMembersCount) : 0
            }));
          }
        } else {
          const err = await response.json().catch(() => ({ error: 'Unknown' }));
          console.warn('useAuth: [fetchProfile] Errore risposta gruppi:', response.status);
          authError = response.status === 403 ? "Permessi Google insufficienti." : (err.error || "Errore gruppi.");
        }
      } catch (e: any) {
        const isTimeout = e.name === 'AbortError' || e.message?.includes('timeout');
        console.warn('useAuth: [fetchProfile] Errore gruppi (non bloccante):', isTimeout ? 'Timeout 4s' : e.message);
        authError = isTimeout ? "Timeout sincronizzazione gruppi." : "Errore di connessione al backend gruppi.";
      }

      // 4. Determinazione Ruolo
      const normalize = (s: string) => s.toLowerCase().replace(/[._\-\s]/g, '');
      const cleanEmail = supabaseUser.email?.trim().toLowerCase() || "";
      const isHardcodedAdmin = cleanEmail === 'diego.cimarosa@gevbologna.org' || cleanEmail === 'admin@gevbologna.org';
      const isAdmin = isHardcodedAdmin || groups.some(g => {
        const n = normalize(g.name);
        return n === 'admins' || n.includes('admin') || n.includes('sistema');
      });

      // Garantiamo la resilienza dei gruppi per l'Amministratore o se le chiamate API falliscono/vanno in timeout
      if (isAdmin) {
        if (!groups.some(g => normalize(g.name) === 'admins')) {
          groups.push({ name: 'admins', email: 'admins@gevbologna.org', role: 'Owner', membersCount: 1 });
        }
        if (!groups.some(g => normalize(g.name) === 'app')) {
          groups.push({ name: 'app', email: 'app@gevbologna.org', role: 'Membro', membersCount: 1 });
        }
      } else if (groups.length === 0 && cleanEmail.endsWith('@gevbologna.org')) {
        // Fallback di cortesia per tutti gli utenti autenticati del dominio se la sincronizzazione fallisce
        groups.push({ name: 'app', email: 'app@gevbologna.org', role: 'Membro', membersCount: 1 });
      }
      
      // 5. Controllo Congruenza Account (Stretto)
      let personaId: number | undefined = undefined;
      const checkResult = await controllo_congruenza_account(supabaseUser.email, supabaseUser.id);
      
      if (!checkResult.success) {
        console.error('useAuth: [fetchProfile] Fallito controllo congruenza:', checkResult.message);
        setLoginError({ 
          title: checkResult.errorType === 'EMAIL_MISSING' ? 'Errore Email' : 'Errore Sincronizzazione',
          message: checkResult.message || 'Errore durante la verifica dell\'account.'
        });
        await supabase.auth.signOut();
        setUser(null);
        return;
      }

      personaId = checkResult.personaId;
      const cognome = checkResult.cognome;
      const nome = checkResult.nome;

      // Calcoliamo il percorso della foto secondo la convenzione Gesvol
      const bucketPhoto = getPersonPhotoPath(cognome, nome);

      if (checkResult.wasUpdated) {
        toast.success('Aggiornato credenziali accesso', {
          description: `Account collegato correttamente a ${supabaseUser.email}`,
          duration: 5000
        });
      }

      // Se è presente un token provider nella sessione Supabase, salviamolo per Google Workspace
      if (session?.provider_token) {
        sessionStorage.setItem('google_access_token', session.provider_token);
        localStorage.setItem('google_access_token', session.provider_token);
        sessionStorage.setItem('google_workspace_authorized', 'true');
        localStorage.setItem('google_workspace_authorized', 'true');
        localStorage.removeItem('google_workspace_authorizing_in_progress');
        sessionStorage.removeItem('google_workspace_authorizing_in_progress');
      }
      if (session?.provider_refresh_token) {
        sessionStorage.setItem('google_refresh_token', session.provider_refresh_token);
        localStorage.setItem('google_refresh_token', session.provider_refresh_token);
      }

      // 6. Creazione Profilo
      
      const profile: UserProfile = {
        id: supabaseUser.id,
        personaId: personaId,
        email: supabaseUser.email,
        full_name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || 'Utente GEV',
        avatar_url: bucketPhoto || supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || supabaseUser.user_metadata?.avatar || supabaseUser.identities?.[0]?.identity_data?.avatar_url,
        hilight_color: supabaseUser.user_metadata?.hilight_color || '#fbbf24', // Default amber-400
        role: isAdmin ? 'Admin' : 'Volontario',
        groups: groups,
        authError: authError,
        permissions: {} // Le autorizzazioni sono ora gestite dinamicamente tramite i gruppi in usePermission
      };
      
      lastFetchedUserId.current = supabaseUser.id;
      setUser(profile);
      successReceivedRef.current = true; // Impostiamo subito per ignorare eventuali SIGNED_OUT spuri
    } catch (error: any) {
      console.error('useAuth: [fetchProfile] Errore fatale:', error);
      setUser(null);
      // Se l'errore è "JWT expired" o simile, forziamo logout
      if (error.message?.includes('expired') || error.status === 401 || error.message?.toLowerCase().includes('refresh token')) {
        supabase.auth.signOut().catch(() => {});
      }
    } finally {
      fetchInProgress.current = null;
      setIsProcessing(false);
      setLoading(false);
    }
  }, []);

  const handleSuccess = React.useCallback(async (sessionFromPopup: any) => {
    if (successReceivedRef.current && !isAuthorizingWorkspaceRef.current) {
      return;
    }
    
    // Reset dello stato di inattività all'accesso
    setShowInactivityModal(false);
    lastActivityRef.current = Date.now();

    successReceivedRef.current = true;
    setIsProcessing(true);
    
    try {
      if (!sessionFromPopup || !sessionFromPopup.access_token) {
        throw new Error('Token mancanti nel messaggio dal popup.');
      }

      // Forziamo l'impostazione della sessione
      const { data, error } = await supabase.auth.setSession({
        access_token: sessionFromPopup.access_token,
        refresh_token: sessionFromPopup.refresh_token || ''
      });

      if (error) {
        console.error('useAuth: [handleSuccess] Errore setSession (tentativo recupero comunque):', error.message);
      }

      const session = data.session || (await supabase.auth.getSession()).data.session;
      
      let gToken = sessionFromPopup.provider_token || session?.provider_token;
      if (!gToken) {
        gToken = localStorage.getItem('google_access_token') || sessionStorage.getItem('google_access_token');
      }
      if (gToken) {
        sessionStorage.setItem('google_access_token', gToken);
        localStorage.setItem('google_access_token', gToken);
        sessionStorage.setItem('google_workspace_authorized', 'true');
        localStorage.setItem('google_workspace_authorized', 'true');
        localStorage.removeItem('google_workspace_authorizing_in_progress');
        sessionStorage.removeItem('google_workspace_authorizing_in_progress');
      } else {
        console.error('useAuth: Google OAuth Token NON trovato nel popup o nella sessione:', {
          hasFromPopup: !!sessionFromPopup.provider_token,
          hasFromSession: !!session?.provider_token
        });
      }
      let gRefreshToken = sessionFromPopup.provider_refresh_token || session?.provider_refresh_token;
      if (!gRefreshToken) {
        gRefreshToken = localStorage.getItem('google_refresh_token') || sessionStorage.getItem('google_refresh_token');
      }
      if (gRefreshToken) {
        sessionStorage.setItem('google_refresh_token', gRefreshToken);
        localStorage.setItem('google_refresh_token', gRefreshToken);
      } else {
        console.error('useAuth: Google OAuth Refresh Token NON trovato nel popup o nella sessione:', {
          hasFromPopup: !!sessionFromPopup.provider_refresh_token,
          hasFromSession: !!session?.provider_refresh_token
        });
      }

      if (session) {
        await fetchProfile(session.user, session, true);
      } else {
        // Fallback: se setSession non ha persistito (cookie bloccati), usiamo getUser con il token
        console.error('useAuth: [handleSuccess] Sessione non persistente, uso fallback getUser');
        const { data: { user: supabaseUser }, error: userError } = await supabase.auth.getUser(sessionFromPopup.access_token);
        if (userError || !supabaseUser) throw userError || new Error('Fallback getUser fallito');
        
        await fetchProfile(supabaseUser, { user: supabaseUser, access_token: sessionFromPopup.access_token }, true);
      }
    } catch (err) {
      console.error('useAuth: [handleSuccess] Errore critico:', err);
      successReceivedRef.current = false; // Permettiamo riprova se fallisce
      setIsProcessing(false);
      setLoading(false);
    } finally {
      isAuthorizingWorkspaceRef.current = false;
      // Watchdog di sicurezza per sbloccare l'interfaccia se qualcosa va storto
      setTimeout(() => {
        if (!userRef.current && successReceivedRef.current && !fetchInProgress.current) {
          console.error('useAuth: [handleSuccess] Watchdog finale (10s): sblocco forzato.');
          successReceivedRef.current = false; // Reset per permettere un nuovo tentativo
          setIsProcessing(false);
          setLoading(false);
        }
      }, 10000);
    }
  }, [fetchProfile]);

  useEffect(() => {
    // 1. Verifica sessione iniziale (UNA SOLA VOLTA)
    
    const sessionTimeout = setTimeout(() => {
      if (loading) {
        console.error('useAuth: [INIT] Timeout getSession iniziale. Sblocco UI.');
        setLoading(false);
      }
    }, 5000);

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      clearTimeout(sessionTimeout);
      if (error) {
        console.error('useAuth: [INIT] Errore getSession:', error);
        if (error.message?.toLowerCase().includes('refresh token')) {
          console.error('useAuth: Invalid refresh token, force logout');
          supabase.auth.signOut().catch(() => {});
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
        }
      }
      if (session) {
        fetchProfile(session.user, session);
      } else {
        setLoading(false);
      }
    });

    // 2. Listeners Auth (UNA SOLA VOLTA)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      
      if (event === 'SIGNED_OUT') {
        // Ignora SIGNED_OUT se:
        // 1. Stiamo processando un successo dal popup
        // 2. Siamo già "null" (evita loop)
        // 3. UserRef ha un utente e l'evento è arrivato troppo in fretta dopo un SIGNED_IN (debounce ghosting)
        if (successReceivedRef.current || isProcessing) {
          return;
        }
        
        if (userRef.current) {
          console.error('useAuth: [onAuthStateChange] Logout rilevato, pulizia stato.');
          setUser(null);
        }
        setLoading(false);
        setIsProcessing(false);
      } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') && session) {
        // Se abbiamo già processato un successo dal popup, evitiamo fetch duplicati
        if (successReceivedRef.current && userRef.current?.id === session.user.id) {
          return;
        }
        fetchProfile(session.user, session);
      }
    });

    // 3. Listeners Messaggi (UNA SOLA VOLTA)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        handleSuccess(event.data.session);
      }
    };

    let authChannel: BroadcastChannel | null = null;
    try {
      authChannel = new BroadcastChannel('supabase_auth');
      authChannel.onmessage = (event) => {
        if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
          handleSuccess(event.data.session);
        }
      };
    } catch (e) {
      console.warn('useAuth: BroadcastChannel non supportato');
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'supabase_auth_success' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          handleSuccess(data.session);
          localStorage.removeItem('supabase_auth_success');
        } catch (e) {
          console.error('useAuth: [MSG] Errore parsing localStorage:', e);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      if (authChannel) authChannel.close();
    };
  }, [fetchProfile, handleSuccess]); // Rimosso 'loading' dalle dipendenze

  const signIn = async () => {
    setIsProcessing(true);
    setLoading(true); // Blocca UI durante l'apertura
    successReceivedRef.current = false;
    
    // Determina il redirectUri corretto
    const redirectUri = `${window.location.origin}/auth/callback`;
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUri,
          skipBrowserRedirect: true,
          queryParams: {
            prompt: 'select_account'
          }
        }
      });

      if (error) throw error;

      if (data?.url) {
        const popup = window.open(data.url, 'oauth_popup', 'width=600,height=700');
        
        if (popup) {
          const checkPopup = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkPopup);
              
              // Watchdog per sbloccare se il messaggio di successo non arriva entro 5 secondi dopo la chiusura
              setTimeout(() => {
                if (!userRef.current && !fetchInProgress.current) {
                  console.error('useAuth: [signIn] Watchdog post-chiusura popup (5s): sblocco forzato');
                  setIsProcessing(false);
                  setLoading(false);
                }
              }, 5000);
            }
          }, 1000);
        } else {
          throw new Error('Popup bloccato dal browser. Abilita i popup per accedere.');
        }
      } else {
        throw new Error('Nessun URL di autenticazione ricevuto da Supabase.');
      }
    } catch (err) {
      console.error('useAuth: [signIn] Errore:', err);
      setIsProcessing(false);
      setLoading(false);
      throw err;
    }
  };

  const authorizeWorkspace = async () => {
    setIsProcessing(true);
    setLoading(true);
    isAuthorizingWorkspaceRef.current = true;
    successReceivedRef.current = false;
    
    localStorage.setItem('google_workspace_authorizing_in_progress', 'true');
    sessionStorage.setItem('google_workspace_authorizing_in_progress', 'true');
    
    const redirectUri = `${window.location.origin}/auth/callback`;
    const scopesList = [
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/contacts.readonly',
      'https://www.googleapis.com/auth/documents',
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/forms.responses.readonly',
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.send',
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/presentations',
      'https://www.googleapis.com/auth/tasks'
    ].join(' ');

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUri,
          skipBrowserRedirect: true,
          scopes: scopesList,
          flowType: 'implicit',
          queryParams: {
            prompt: 'consent',
            access_type: 'offline'
          }
        } as any
      });

      if (error) throw error;

      if (data?.url) {
        const popup = window.open(data.url, 'oauth_popup', 'width=600,height=700');
        if (popup) {
          const checkPopup = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkPopup);
              setTimeout(() => {
                setIsProcessing(false);
                setLoading(false);
                isAuthorizingWorkspaceRef.current = false;
              }, 4000);
            }
          }, 1000);
        } else {
          throw new Error('Popup bloccato dal browser. Abilita i popup.');
        }
      } else {
        throw new Error('Nessun URL ricevuto.');
      }
    } catch (err) {
      console.error('useAuth: [authorizeWorkspace] Errore:', err);
      setIsProcessing(false);
      setLoading(false);
      isAuthorizingWorkspaceRef.current = false;
      throw err;
    }
  };

  // --- FAIL-SAFE: RESET STATI QUANDO L'UTENTE È PRESENTE ---
  useEffect(() => {
    if (user) {
      setIsProcessing(false);
      setLoading(false);
      successReceivedRef.current = true;
    }
  }, [user]);

  // --- WATCHDOG: SBLOCCO FORZATO DOPO 20 SECONDI ---
  useEffect(() => {
    let watchdog: ReturnType<typeof setTimeout>;
    if (isProcessing || loading) {
      watchdog = setTimeout(() => {
        // Non resettare se un fetch è in corso!
        if (!fetchInProgress.current) {
          console.warn('useAuth: [WATCHDOG] Caricamento bloccato da troppo tempo (20s). Forzo sblocco.');
          setIsProcessing(false);
          setLoading(false);
        }
      }, 20000);
    }
    return () => clearTimeout(watchdog);
  }, [isProcessing, loading]);

  const refreshProfile = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (session) {
        await fetchProfile(session.user, session, true);
      }
    } catch (error: any) {
      console.error('useAuth: Errore in refreshProfile:', error);
      if (error.message?.toLowerCase().includes('refresh token')) {
        signOut('timeout');
      }
    }
  };

  const signOut = async (reason: 'manual' | 'timeout' = 'manual') => {
    try {
      // Impediamo loop o doppie esecuzioni
      if (typeof window !== 'undefined' && (window as any).is_signing_out) {
        return;
      }
      (window as any).is_signing_out = true;

      // 1. Reset locale IMMEDIATO per fermare ogni attività
      setIsProcessing(false);
      successReceivedRef.current = false;
      fetchInProgress.current = null;
      lastFetchedUserId.current = null;
      setUser(null);
      setLoading(true);

      // 2. Pulizia storage totale
      // Rimuoviamo TUTTE le chiavi legate a Supabase o al progetto
      if (typeof window !== 'undefined') {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (
            key.startsWith('sb-') || 
            key.includes('supabase') || 
            key === 'gev-bologna-auth' || 
            key === 'supabase_auth_success' ||
            key === 'is_signing_out' ||
            key === 'google_access_token' ||
            key === 'google_refresh_token' ||
            key === 'google_workspace_authorized'
          ) {
            localStorage.removeItem(key);
          }
        });
        
        sessionStorage.clear();
      }

      // 3. Notifica Supabase con scope 'global' per invalidare sessioni ovunque
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (e) {
        console.error('useAuth: Errore signOut Supabase (ignorato):', e);
      }

      // 4. Hard reload per reset completo dell'istanza React e dello stato del browser/iframe
      
      // Piccola pausa per assicurarsi che i reset locali e storage siano propagati
      setTimeout(() => {
        delete (window as any).is_signing_out;
        // Redirigi alla login con un cache-buster per AI Studio
        window.location.href = `/login?logout=${Date.now()}`;
      }, 300);
      
    } catch (err) {
      console.error('useAuth: Errore critico signOut:', err);
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  };



  // --- LOGICA TIMEOUT INATTIVITÀ ---
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  // Default a 120 secondi per il countdown finale quando la modale è aperta
  const [countdown, setCountdown] = useState(120); 
  const lastActivityRef = React.useRef<number>(Date.now());
  
  // Default a 60 minuti (3600 secondi) se non specificato.
  // In ambiente di sviluppo aumentiamo a 12 ore per evitare disconnessioni fastidiose durante la programmazione.
  const timeoutSeconds = useMemo(() => {
    if (import.meta.env.DEV) {
      return 12 * 3600; // 12 ore
    }
    const envTimeout = import.meta.env.VITE_INACTIVITY_TIMEOUT;
    const parsed = parseInt(envTimeout || '3600');
    return isNaN(parsed) || parsed <= 0 ? 3600 : parsed;
  }, []);

  useEffect(() => {
    if (!user) {
      setShowInactivityModal(false);
      try {
        localStorage.removeItem('gesvol_last_activity');
      } catch (e) {}
      return;
    }

    const nowTimestamp = Date.now();
    lastActivityRef.current = nowTimestamp;
    try {
      localStorage.setItem('gesvol_last_activity', nowTimestamp.toString());
    } catch (e) {}

    const handleActivity = () => {
      const currentTimestamp = Date.now();
      lastActivityRef.current = currentTimestamp;
      try {
        localStorage.setItem('gesvol_last_activity', currentTimestamp.toString());
      } catch (e) {}
      if (showInactivityModal) {
        setShowInactivityModal(false);
        setCountdown(120);
      }
    };

    // Listeners per l'attività dell'utente - Aggiunti pointermove e click per maggiore precisione
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'pointermove', 'click', 'wheel', 'visibilitychange'];
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    // Timer per controllare l'inattività
    const activityInterval = setInterval(() => {
      if (showInactivityModal) return; // Se la modale è aperta, il countdown è gestito dall'altro effetto

      const now = Date.now();
      let lastAct = lastActivityRef.current;
      
      // Sincronizziamo l'attività dell'utente tra diversi tab o iframe
      try {
        const storedActivity = localStorage.getItem('gesvol_last_activity');
        if (storedActivity) {
          const parsed = parseInt(storedActivity);
          if (!isNaN(parsed)) {
            lastAct = parsed;
          }
        }
      } catch (e) {}

      const inactiveTime = (now - lastAct) / 1000;
      const inactiveSeconds = Math.floor(inactiveTime);

      if (inactiveTime > timeoutSeconds) {
        console.warn(`useAuth: Inattività rilevata (${inactiveSeconds}s > ${timeoutSeconds}s). Apertura modale...`);
        setShowInactivityModal(true);
        setCountdown(120); 
      }
    }, 5000); // Controllo ogni 5 secondi invece di 1 per performance

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearInterval(activityInterval);
    };
  }, [user, showInactivityModal, timeoutSeconds]);

  // Timer per il countdown della modale
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (showInactivityModal) {
      if (countdown > 0) {
        timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      } else {
        console.error('useAuth: Timeout inattività scaduto. Logout automatico...');
        signOut('timeout');
      }
    }
    return () => clearTimeout(timer);
  }, [showInactivityModal, countdown]);

  return (
    <AuthContext.Provider value={{ user, loading, isProcessing, signIn, signOut, refreshProfile, authorizeWorkspace }}>
      {children}
      
      {/* MODALE ERRORE LOGIN (BLOCCANTE) */}
      {loginError && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="bg-white dark:bg-zinc-900 border border-red-200 dark:border-red-900/30 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600 dark:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-red-600 dark:text-red-500 mb-2">
              {loginError.title}
            </h3>
            <p className="text-zinc-700 dark:text-zinc-300 mb-8 whitespace-pre-wrap">
              {loginError.message}
            </p>
            <button
              onClick={async () => {
                setLoginError(null);
                // Pulizia radicale prima di tornare al login
                if (typeof window !== 'undefined') {
                  localStorage.clear();
                  sessionStorage.clear();
                  try {
                    await supabase.auth.signOut({ scope: 'global' });
                  } catch (e) {}
                }
                window.location.href = `/login?error_reset=${Date.now()}`;
              }}
              className="w-full py-3 px-6 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors shadow-lg shadow-red-900/20"
            >
              Torna al Login
            </button>
          </div>
        </div>
      )}

      {/* MODALE INATTIVITÀ */}
      {showInactivityModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-600 dark:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
              Sessione in scadenza
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-8">
              Logoff automatico per inattività in <span className="font-mono font-bold text-amber-600 dark:text-amber-500 text-lg">{countdown}</span> secondi.
            </p>
            <button
              onClick={() => {
                lastActivityRef.current = Date.now();
                setShowInactivityModal(false);
                setCountdown(120);
              }}
              className="w-full py-3 px-6 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-semibold hover:opacity-90 transition-opacity"
            >
              Continua a lavorare
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
